import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  Link,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  Spinner,
  Subtitle1,
  Subtitle2,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import type { ReadinessResultRecord } from '@lg/core';
import type { ReadinessCase, ReadinessLayer } from '@lg/shared';
import { useApi } from '../api/client';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { dateTime, errorMessage } from '../ui/format';
import {
  KIND_LABEL,
  LAYER_LABEL,
  LAYER_QUESTION,
  RESULT_COLOR,
  RUN_COLOR,
  duration,
  evidenceLink,
  expectText,
  personaLabel,
} from '../readiness/format';

const useStyles = makeStyles({
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  card: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  head: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacingHorizontalM },
  spacer: { flex: 1 },
  facts: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXXL}`,
  },
  fact: { display: 'flex', flexDirection: 'column' },
  factValue: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    fontVariantNumeric: 'tabular-nums',
  },
  wrap: { overflowX: 'auto' },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200, whiteSpace: 'nowrap' },
  sub: { display: 'block', color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  error: { display: 'block', color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200 },
  demoRow: { backgroundColor: tokens.colorPaletteYellowBackground1 },
  nowrap: { whiteSpace: 'nowrap' },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', alignItems: 'center' },
});

const LAYERS: ReadinessLayer[] = ['L1', 'L2', 'L3', 'L4'];

export function ReadinessTab({ requestId }: { requestId: string }) {
  const styles = useStyles();
  const api = useApi();
  const qc = useQueryClient();
  const view = useQuery({
    queryKey: ['readiness', requestId],
    queryFn: () => api.getReadiness(requestId),
    // Poll while a run is reporting results.
    refetchInterval: (q) =>
      ['Running', 'Queued'].includes(q.state.data?.latestRun?.status ?? '') ? 1000 : false,
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['readiness', requestId] });
  const generate = useMutation({
    mutationFn: (env: 'TEST' | 'PROD') => api.generateReadinessPlan(requestId, env),
    onSuccess: (v) => qc.setQueryData(['readiness', requestId], v),
  });
  const run = useMutation({
    mutationFn: (planId: string) => api.startReadinessRun(planId),
    onSuccess: refresh,
  });

  if (view.isPending) return <Spinner label="Loading readiness…" />;
  if (view.isError)
    return (
      <MessageBar intent="error">
        <MessageBarBody>{errorMessage(view.error)}</MessageBarBody>
      </MessageBar>
    );
  const v = view.data;
  const plan = v.plan;
  const resultOf = new Map<string, ReadinessResultRecord>(v.results.map((r) => [r.caseId, r]));
  const latest = v.latestRun;
  const running = latest && (latest.status === 'Running' || latest.status === 'Queued');
  const total = LAYERS.reduce((n, l) => n + v.countsByLayer[l], 0);

  return (
    <div className={styles.stack}>
      <Card className={styles.card}>
        <div className={styles.head}>
          <Subtitle1 as="h2">Entity readiness</Subtitle1>
          {plan && (
            <Badge appearance="outline" color={plan.environment === 'PROD' ? 'danger' : 'brand'}>
              D365 {plan.environment}
            </Badge>
          )}
          {latest && (
            <Badge appearance="filled" color={RUN_COLOR[latest.status]}>
              {latest.status === 'Running'
                ? `Running ${latest.passed + latest.failed + latest.skipped}/${latest.total}`
                : latest.status}
            </Badge>
          )}
          <span className={styles.spacer} />
          {v.canManage && (
            <div className={styles.actions}>
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <Button disabled={!!v.cannotGenerate || generate.isPending}>
                    {plan ? 'Regenerate plan' : 'Generate plan'}
                  </Button>
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem onClick={() => generate.mutate('TEST')}>Full plan for D365 TEST</MenuItem>
                    <MenuItem onClick={() => generate.mutate('PROD')}>Read-only checks for PROD</MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
              <Button
                appearance="primary"
                disabled={!plan || !!v.cannotRun || run.isPending}
                onClick={() => plan && run.mutate(plan.id)}
              >
                {running ? 'Running…' : `Run in ${plan?.environment ?? 'TEST'}`}
              </Button>
            </div>
          )}
        </div>
        <Body1>
          The app writes this plan from what it knows about the venue: its modules, the roles in the people
          import, the approval chain from the routing preview and the template&apos;s governed settings.
          Playwright runs each case in D365 as a test persona seated in the venue&apos;s positions.
        </Body1>
        {plan ? (
          <div className={styles.facts}>
            <Fact label="Cases" value={String(total)} />
            {LAYERS.map((l) => (
              <Fact key={l} label={`${l} ${LAYER_LABEL[l]}`} value={String(v.countsByLayer[l])} />
            ))}
            {latest && (
              <>
                <Fact label="Passed" value={String(latest.passed)} />
                <Fact label="Failed" value={String(latest.failed)} />
                {latest.waived > 0 && <Fact label="Waived" value={String(latest.waived)} />}
                <Fact label="Test time" value={duration(latest.durationMs)} />
                <Fact label="Last run" value={latest.startedAt ? dateTime(latest.startedAt) : '—'} />
              </>
            )}
          </div>
        ) : (
          <MessageBar intent="info">
            <MessageBarBody>
              {v.cannotGenerate ??
                (v.canManage
                  ? 'No plan yet. Generate one to see what the readiness run will prove.'
                  : 'No plan yet. A D365 administrator generates it.')}
            </MessageBarBody>
          </MessageBar>
        )}
        {plan && v.cannotRun && v.canManage && <Caption1>{v.cannotRun}</Caption1>}
        {(generate.isError || run.isError) && (
          <MessageBar intent="error">
            <MessageBarBody>{errorMessage(generate.error ?? run.error)}</MessageBarBody>
          </MessageBar>
        )}
      </Card>

      {plan &&
        LAYERS.filter((l) => v.countsByLayer[l] > 0 || plan.plan.cases.some((c) => c.layer === l)).map(
          (layer) => (
            <LayerCard
              key={layer}
              layer={layer}
              cases={plan.plan.cases.filter((c) => c.layer === layer)}
              resultOf={resultOf}
              canWaive={v.canManage}
              onWaived={refresh}
            />
          ),
        )}

      {plan && (
        <Card className={styles.card}>
          <Subtitle2 as="h3">Setup data</Subtitle2>
          <Caption1>
            Checked by the preflight at the start of every run; a missing item stops the run with one message.
          </Caption1>
          <div className={styles.wrap}>
            <Table size="small" aria-label="Setup data">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Need</TableHeaderCell>
                  <TableHeaderCell>Source</TableHeaderCell>
                  <TableHeaderCell>Check</TableHeaderCell>
                  <TableHeaderCell>Last preflight</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {v.setup.map((s) => (
                  <TableRow key={s.key}>
                    <TableCell>{s.need}</TableCell>
                    <TableCell>{s.source}</TableCell>
                    <TableCell>{s.check}</TableCell>
                    <TableCell>
                      <Badge
                        appearance="tint"
                        color={s.status === 'ok' ? 'success' : s.status === 'missing' ? 'danger' : 'subtle'}
                      >
                        {s.status === 'ok' ? 'ok' : s.status === 'missing' ? 'missing' : 'not checked'}
                      </Badge>
                      <span className={styles.sub}>{s.detail}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {v.runs.length > 0 && (
        <Card className={styles.card}>
          <Subtitle2 as="h3">Runs</Subtitle2>
          <div className={styles.wrap}>
            <Table size="small" aria-label="Readiness runs">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Started</TableHeaderCell>
                  <TableHeaderCell>Environment</TableHeaderCell>
                  <TableHeaderCell>D365</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Passed</TableHeaderCell>
                  <TableHeaderCell>Failed</TableHeaderCell>
                  <TableHeaderCell>Skipped</TableHeaderCell>
                  <TableHeaderCell>Test time</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {v.runs.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className={styles.nowrap}>
                      {r.startedAt ? dateTime(r.startedAt) : '—'}
                    </TableCell>
                    <TableCell>{r.environment}</TableCell>
                    <TableCell>{r.d365Version ?? '—'}</TableCell>
                    <TableCell>
                      <Badge appearance="tint" color={RUN_COLOR[r.status]}>
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{r.passed}</TableCell>
                    <TableCell>{r.failed + r.waived}</TableCell>
                    <TableCell>{r.skipped}</TableCell>
                    <TableCell>{duration(r.durationMs)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  const styles = useStyles();
  return (
    <span className={styles.fact}>
      <Caption1>{label}</Caption1>
      <span className={styles.factValue}>{value}</span>
    </span>
  );
}

function LayerCard({
  layer,
  cases,
  resultOf,
  canWaive,
  onWaived,
}: {
  layer: ReadinessLayer;
  cases: ReadinessCase[];
  resultOf: ReadonlyMap<string, ReadinessResultRecord>;
  canWaive: boolean;
  onWaived: () => void;
}) {
  const styles = useStyles();
  const api = useApi();
  // Plan order: per role for access, and create → route → approve per amount for process.
  const sorted = cases;
  return (
    <Card className={styles.card}>
      <Subtitle2 as="h3">
        {layer} · {LAYER_LABEL[layer]}
      </Subtitle2>
      <Caption1>{LAYER_QUESTION[layer]}</Caption1>
      <div className={styles.wrap}>
        <Table size="small" aria-label={`${layer} cases`}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={{ width: '34%' }}>Case</TableHeaderCell>
              <TableHeaderCell style={{ width: '11%' }}>Persona</TableHeaderCell>
              <TableHeaderCell style={{ width: '27%' }}>Expected result</TableHeaderCell>
              <TableHeaderCell>Where</TableHeaderCell>
              <TableHeaderCell>Result</TableHeaderCell>
              <TableHeaderCell>Evidence</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((c) => {
              const r = resultOf.get(c.id);
              const link = r ? evidenceLink(r) : null;
              return (
                <TableRow key={c.id} className={mergeClasses(c.demo && styles.demoRow)}>
                  <TableCell>
                    {c.title}
                    <span className={mergeClasses(styles.sub, styles.mono)}>
                      {c.id} · {c.template}
                    </span>
                    {r?.errorSummary && r.status !== 'skipped' && (
                      <span className={styles.error}>
                        {r.failureKind ? `${KIND_LABEL[r.failureKind] ?? r.failureKind}: ` : ''}
                        {r.errorSummary}
                      </span>
                    )}
                    {r?.status === 'skipped' && <span className={styles.sub}>{r.errorSummary}</span>}
                    {r?.waivedBy && (
                      <span className={styles.sub}>
                        Waived by {r.waivedBy}: {r.waiveReason}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{personaLabel(c.persona)}</TableCell>
                  <TableCell>{expectText(c)}</TableCell>
                  <TableCell className={styles.nowrap}>{c.envs.join(' + ')}</TableCell>
                  <TableCell className={styles.nowrap}>
                    {r ? (
                      <>
                        <Badge appearance="tint" color={r.waivedBy ? 'warning' : RESULT_COLOR[r.status]}>
                          {r.waivedBy ? 'waived' : r.status}
                        </Badge>
                        {r.status !== 'skipped' && (
                          <span className={styles.sub}>{duration(r.durationMs)}</span>
                        )}
                      </>
                    ) : (
                      <Caption1>not run</Caption1>
                    )}
                  </TableCell>
                  <TableCell className={styles.nowrap}>
                    {link && (
                      <Link href={link} target="_blank" rel="noopener">
                        Trace
                      </Link>
                    )}
                    {r?.status === 'failed' && !r.waivedBy && canWaive && (
                      <>
                        {link && ' · '}
                        <ConfirmDialog
                          trigger={
                            <Link as="button" appearance="subtle">
                              Waive
                            </Link>
                          }
                          title={`Waive ${c.id}`}
                          confirmLabel="Waive failure"
                          input={{
                            kind: 'comment',
                            label: 'Reason (kept on the result and in the audit log)',
                            required: true,
                          }}
                          onConfirm={async (reason) => {
                            await api.waiveReadinessResult(r.id, reason);
                            onWaived();
                          }}
                        >
                          <Body1>
                            The failure stays on record with your name and reason. Fix the cause instead when
                            the venue&apos;s users would hit it.
                          </Body1>
                        </ConfirmDialog>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
